import { Alert, Button } from 'react-bootstrap'
import { useMe } from './me'
import FeeButton, { FeeButtonProvider } from './fee-button'
import { piconerosToMXmr } from '@/lib/format'
import { Form } from './form'
import { timeSince } from '@/lib/time'
import { LongCountdown } from './countdown'
import { useCallback, useState } from 'react'
import { useApolloClient } from '@apollo/client/react'
import { nextBillingWithGrace } from '@/lib/territory'
import usePayInMutation from '@/components/payIn/hooks/use-pay-in-mutation'
import { throwUnlessUserCancel } from '@/wallets/client/errors'
import { SUB_PAY } from '@/fragments/payIn'
import { useShowModal } from './modal'
import TerritoryPendingFeeModal from './territory-pending-fee-modal'

export default function TerritoryPaymentDue ({ sub }) {
  const { me } = useMe()
  const client = useApolloClient()
  const [paySub] = usePayInMutation(SUB_PAY)

  const onSubmit = useCallback(async ({ ...variables }) => {
    const { error, payError } = await paySub({
      variables
    })

    if (error) throw error
    // territory billing is pessimistic, so a terminal payment failure comes back in payError —
    // but a user-canceled QR isn't news
    throwUnlessUserCancel(payError)
  }, [client, paySub])

  if (!sub || sub.userId !== Number(me?.id)) return null

  // a freshly-created or renewed territory whose fee hasn't been observed yet is
  // ACTIVE + PENDING_FEE — show a re-pay button so the founder can pay even from
  // the live territory page
  if (sub.billingStatus === 'PENDING_FEE') {
    return <PendingFeeRepay sub={sub} />
  }

  if (sub.status === 'ACTIVE') return null

  const dueDate = nextBillingWithGrace(sub)
  if (!dueDate) return null

  const feePiconeros = BigInt({
    MONTHLY: me?.privates?.territoryMonthlyPiconeros,
    YEARLY: me?.privates?.territoryYearlyPiconeros,
    ONCE: me?.privates?.territoryOncePiconeros
  }[sub.billingType] || 0)

  return (
    <Alert key='danger' variant='danger'>
      {sub.status === 'STOPPED'
        ? (
          <>
            <Alert.Heading>
              Your ~{sub.name} turf has been archived!
            </Alert.Heading>
            <div>
              Make a payment to reactivate it.
            </div>
          </>)
        : (
          <>
            <Alert.Heading>
              Your ~{sub.name} turf payment is due!
            </Alert.Heading>
            <div>
              Your turf will be archived in <LongCountdown date={dueDate} />otherwise.
            </div>
          </>
          )}

      <FeeButtonProvider
        baseLineItems={{
          territory: {
            term: `+ ${piconerosToMXmr(feePiconeros)}`,
            label: `${sub.billingType.toLowerCase()} turf fee`,
            op: '+',
            modifier: cost => cost + Number(feePiconeros / 1000n)
          }
        }}
      >
        <Form
          initial={{
            name: sub.name
          }}
          onSubmit={onSubmit}
        >
          <div className='d-flex justify-content-end'>
            <FeeButton
              text='pay'
              variant='success'
            />
          </div>
        </Form>
      </FeeButtonProvider>
    </Alert>
  )
}

function PendingFeeRepay ({ sub }) {
  const showModal = useShowModal()
  const [paySub] = usePayInMutation(SUB_PAY)
  const [submitting, setSubmitting] = useState(false)

  const onPay = useCallback(async () => {
    setSubmitting(true)
    try {
      const { data, error, payError } = await paySub({ variables: { name: sub.name } })
      if (error) throw error
      if (payError) return
      const response = data?.paySub
      // Open on a URI-bearing response (still owed) OR a counter-bearing
      // response with a NULL URI — the fully-observed-but-not-flipped state
      // (the modal renders "waiting for confirmation", never a re-quote). The
      // bare ACTIVE early-return has neither, so it stays modal-free.
      if (response?.moneroUri || response?.expectedPiconeros != null) {
        showModal(onClose => (
          <TerritoryPendingFeeModal
            moneroUri={response.moneroUri}
            subName={sub.name}
            receivedPiconeros={response.receivedPiconeros}
            expectedPiconeros={response.expectedPiconeros}
            onClose={onClose}
          />
        ))
      }
    } catch (e) {
      console.error('failed to pay turf fee', e)
    } finally {
      setSubmitting(false)
    }
  }, [paySub, sub.name, showModal])

  return (
    <Alert key='warning' variant='warning'>
      <Alert.Heading>Your ~{sub.name} turf fee is pending payment.</Alert.Heading>
      <div>The turf is live once the Monero fee is detected on-chain.</div>
      <div className='d-flex justify-content-end mt-2'>
        <Button variant='success' disabled={submitting} onClick={onPay}>
          {submitting ? 'generating…' : 'pay turf fee'}
        </Button>
      </div>
    </Alert>
  )
}

export function TerritoryBillingLine ({ sub }) {
  const { me } = useMe()
  if (!sub || sub.userId !== Number(me?.id)) return null

  const dueDate = sub.billPaidUntil && new Date(sub.billPaidUntil)
  const pastDue = dueDate && dueDate < new Date()

  return (
    <div className='text-muted'>
      <span>billing {sub.billingAutoRenew ? 'reminders on' : 'reminders off'} </span>
      <span className='fw-bold' suppressHydrationWarning>{pastDue ? 'past due' : dueDate ? timeSince(dueDate) : 'never again'}</span>
    </div>
  )
}
